/* istanbul ignore file */
import React, { useCallback, useState, useEffect, useRef } from 'react'
import logger from '@adc/logger'
import gql from 'graphql-tag'
import {
  Button,
  useApplicationContext,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
  // @ts-ignore no types
} from '@adc/ui-adc'
import type {
  AuctionBlueprintScheduledOnlineReactiveArgs,
  primitives,
} from '@adc/blueprint'
import interpolate from '../../../utils/interpolate'
import { ZERO_DOLLARS } from '../constants'
import { formatMoneyWithNumeral } from '../../../utils/formatters'
import analytics from '../../../services/analytics'
import MatchBidConfirmationModal from './matchBidConfirmationModal'

type AuctionUser = primitives.AuctionUser

const MATCH_BID_ERROR = 'There was an error handling your request.'
const TRANSIENT_MESSAGE_MATCH_BID_WAS_PLACED =
  'You have successfully matched the winning bid of %s.'

interface MatchWinningBidButtonProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  calculations: any
  handleMatchBidSubmittedSuccess: any
  user: AuctionUser
  isMatchBidSubmitted: boolean
  upsertReoPurchaseProfile: any
}

const MatchWinningBidButton = ({
  user,
  isMatchBidSubmitted,
  calculations,
  segment,
  handleMatchBidSubmittedSuccess,
  upsertReoPurchaseProfile,
}: MatchWinningBidButtonProps) => {
  const { graphWebsocket } = useApplicationContext()
  const notifications = useApplicationNotifications()

  const currentBidAmount = segment.current_highest_bid?.bid_amount ?? 0
  const currentUserBidAmount = segment.current_user_highest_bid?.bid_amount ?? 0
  const purchaseProfiles = (user.logged_in && user.purchase_profiles) || []

  const [isBidConfirmationOpen, setBidConfirmationOpen] = useState(false)

  const [isPerformingBid, setIsPerformingBid] = useState(false)

  const handleClick = useCallback(() => {
    analytics.matchBidding.matchWinningBidButtonClick('success')
    setBidConfirmationOpen(true)
  }, [setBidConfirmationOpen])

  const handleClose = useCallback(
    () => setBidConfirmationOpen(false),
    [setBidConfirmationOpen]
  )

  // TODO: this function is very similar to one defined in bidform, find some way to consolidate them
  const showMatchBidAttemptToast = (message: any, type: string) => {
    if (type === NOTIFICATION_ROLES.SUCCESS) {
      notifications.overlayBanner.open({
        message,
        title: 'Thank You!',
      })
    } else if (type === NOTIFICATION_ROLES.ERROR) {
      notifications.overlayBanner.open({
        message,
        title: 'Match Bid Error',
        role: NOTIFICATION_ROLES.ERROR,
      })
    }
  }

  const handleSubmit = async (selectedPurchaseProfileId: any) => {
    setIsPerformingBid(true)

    try {
      await segment.__methods__.submitOnlineMatchBid({
        offerTemplateId: selectedPurchaseProfileId,
      })

      analytics.matchBidding.matchWinningBidConfirmationClick('success')
      handleMatchBidSubmittedSuccess()

      const formattedCurrentBidAmount = formatMoneyWithNumeral(
        currentBidAmount,
        ZERO_DOLLARS
      )
      const message = interpolate(TRANSIENT_MESSAGE_MATCH_BID_WAS_PLACED, [
        formattedCurrentBidAmount,
      ])
      showMatchBidAttemptToast(message, NOTIFICATION_ROLES.SUCCESS)
    } catch (err) {
      analytics.matchBidding.matchWinningBidConfirmationClick('fail')

      showMatchBidAttemptToast(MATCH_BID_ERROR, NOTIFICATION_ROLES.SUCCESS)
    } finally {
      setBidConfirmationOpen(false)
      setIsPerformingBid(false)
    }
  }

  const closeCurrentOperation = useRef()

  useEffect(() => {
    ;(closeCurrentOperation as any).current?.()

    if (isMatchBidSubmitted) {
      return
    }

    graphWebsocket
      .subscribe({
        operationOptions: {
          query: gql`
            subscription resiPdp_reoPurchaseProfileUpdated {
              reoPurchaseProfileUpdated {
                action
                reo_purchase_profile {
                  offer_template_id
                  template_name
                  percent_complete
                }
              }
            }
          `,
        },
        onData: (data: any) => {
          const profileData = data?.reoPurchaseProfileUpdated

          if (profileData) {
            upsertReoPurchaseProfile(profileData)
          }
        },
        onError: (error: any) => {
          logger.warn(error)
        },
      })
      .then((graphSubscriber: any) => {
        closeCurrentOperation.current = graphSubscriber.unsubscribe
        return true
      })
      .catch((err: any) => logger.warn(err))
    return () => (closeCurrentOperation as any).current?.()
  }, [graphWebsocket, upsertReoPurchaseProfile, isMatchBidSubmitted])

  return (
    <>
      <Button
        disabled={isMatchBidSubmitted}
        onClick={handleClick}
        elmId='match_winning_bid_button'
        isStretch
        role='primary'
      >
        {isMatchBidSubmitted ? 'Bid Matched' : 'Match Winning Bid'}
      </Button>
      <MatchBidConfirmationModal
        onConfirm={handleSubmit}
        isPerformingBid={isPerformingBid}
        onClose={handleClose}
        open={isBidConfirmationOpen}
        purchaseProfiles={purchaseProfiles as any[]}
        currentBidAmount={currentBidAmount}
        currentUserBidAmount={currentUserBidAmount}
        calculations={calculations}
      />
    </>
  )
}

export default MatchWinningBidButton
